import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from '../../api/axios';
import TutorialCarousel from './TutorialCarousel';
import { tutorialContent, TUTORIAL_ROLES, markTutorialSeen } from './tutorialContent';

const TutorialModal = ({ open, onClose, initialRole = null }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [role, setRole] = useState(initialRole);

  useEffect(() => {
    if (open) setRole(initialRole);
  }, [open, initialRole]);

  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState('');

  const handleFinish = () => {
    if (role) markTutorialSeen(role);
    onClose();
  };

  const handleCta = async (slide) => {
    if (slide.actionKind === 'seedDemo') {
      setSeeding(true);
      setSeedError('');
      try {
        const response = await axios.post('/api/projects/seed-demo');
        const newId = response.data?.project?.id;
        if (role) markTutorialSeen(role);
        onClose();
        if (newId) navigate(`/project/${newId}`);
      } catch (err) {
        const data = err.response?.data || {};
        const parts = [
          data.error || 'Failed to create the sample project.',
          data.detail ? `Detail: ${data.detail}` : null,
          data.code ? `Code: ${data.code}` : null,
          data.constraint ? `Constraint: ${data.constraint}` : null,
        ].filter(Boolean);
        setSeedError(parts.join(' · '));
      } finally {
        setSeeding(false);
      }
      return;
    }
    if (role) markTutorialSeen(role);
    onClose();
    if (slide.ctaPath) navigate(slide.ctaPath);
  };

  const slides = role ? tutorialContent[role] : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          minHeight: { xs: '100vh', sm: 620 },
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={onClose} aria-label="Close tutorial">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {!role ? (
          <Box sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              How Congrow works
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 480, mx: 'auto' }}>
              Pick the role that fits you to see the right walkthrough.
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {TUTORIAL_ROLES.map(({ key, label, icon: Icon, color }) => (
                <Grid item xs={12} sm={4} key={key}>
                  <Paper
                    elevation={2}
                    onClick={() => setRole(key)}
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      borderTop: `4px solid ${color}`,
                      textAlign: 'center',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                    }}
                  >
                    <Icon sx={{ fontSize: 48, color, mb: 1 }} />
                    <Typography variant="h6" fontWeight={600}>{label}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {seedError && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setSeedError('')}>
                {seedError}
              </Alert>
            )}
            <TutorialCarousel slides={slides} onFinish={handleFinish} onCta={handleCta} />
            {seeding && (
              <Box
                sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TutorialModal;
